import { useNavigate, useRevalidator } from "react-router";
import { Box, Container, Button, TextField, FormLabel, MenuItem } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { Loading } from "src/components/utils/Loading";
import { BrandWrite, BrandWriteSchema, useCreateBrand } from "src/api/client-api/brand/useCreateBrand";
import { useGetAllOrganization } from "src/api/client-api/company/useGetAllOrganization";
import { Brand } from "src/api/client-api/brand/useGetOneBrand";
import { URLHandler } from "src/services/URLHandler";

export type BrandCreateProps = {
    existingBrand?: Brand;
};

export const BrandCreate = (props: BrandCreateProps) => {
    const { existingBrand } = props;

    const navigate = useNavigate();
    const revalidator = useRevalidator();
    const { t } = useTranslation();

    const createBrand = useCreateBrand();
    const { companies = [] } = useGetAllOrganization({
        itemsPerPage: 200,
        isProvider: false,
    });

    const {
        control,
        handleSubmit,
        formState: { errors },
    } = useForm<BrandWrite>({
        mode: "onChange",
        resolver: zodResolver(BrandWriteSchema as any),
    });

    const onSubmitHandler = (values: BrandWrite) => {
        createBrand.mutate(
            { brand: values },
            {
                onSuccess: () => {
                    revalidator.revalidate();
                    navigate(URLHandler.settings.brands.list());
                },
                onError: (_error) => {},
            },
        );
    };

    if (companies === undefined) return <Loading />;

    return (
        <Container
            component="main"
            maxWidth={false}
            sx={{
                m: "0",
                p: "5rem !important",
            }}
        >
            <h1>{"Brands"}</h1>
            <form onSubmit={handleSubmit(onSubmitHandler)}>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "rows",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginTop: "1rem",
                        marginBottom: "1rem",
                    }}
                >
                    <Controller
                        name="id"
                        control={control}
                        defaultValue={existingBrand?.id}
                        render={({ field: { ...field } }) => <input type="hidden" {...field}></input>}
                    />
                    <Controller
                        name="name"
                        control={control}
                        defaultValue={existingBrand?.name}
                        render={({ field: { ref, ...field } }) => (
                            <TextField
                                sx={{ width: "49%" }}
                                label={"Nom"}
                                type="text"
                                variant="outlined"
                                error={Boolean(errors.name)}
                                helperText={errors.name?.message}
                                inputRef={ref}
                                {...field}
                            />
                        )}
                    />
                </Box>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "rows",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginTop: "1rem",
                        marginBottom: "1rem",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            minWidth: "400px",
                            flexDirection: "column",
                        }}
                    >
                        <FormLabel>{t("entreprise_parent")}</FormLabel>
                        <Controller
                            name="organization"
                            control={control}
                            defaultValue={existingBrand?.organization?.["@id"]}
                            render={({ field: { ref, ...field } }) => (
                                <TextField
                                    select
                                    variant="outlined"
                                    inputRef={ref}
                                    error={Boolean(errors.organization)}
                                    helperText={errors.organization?.message}
                                    {...field}
                                >
                                    {companies &&
                                        companies.flatMap((company, index) => {
                                            if (company["@id"] === existingBrand?.["@id"]) {
                                                return [];
                                            }
                                            return [
                                                <MenuItem key={index} value={company["@id"]}>
                                                    {company.name}
                                                </MenuItem>,
                                            ];
                                        })}
                                </TextField>
                            )}
                        />
                    </Box>
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <Button sx={{ marginTop: "1rem", width: "10rem" }} variant="primary" type="submit">
                        {t("enregistrer")}
                    </Button>
                </Box>
            </form>
        </Container>
    );
};
